import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Gamepad2, ArrowLeft, ShoppingCart } from "lucide-react";
import GameCard from "@/components/parent-games/GameCard";
import GamePlayView from "@/components/parent-games/GamePlayView";

export default function ParentGames() {
  const { ptaId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ptaData, setPtaData] = useState<any>(null);
  const [games, setGames] = useState<any[]>([]);
  const [validationEntries, setValidationEntries] = useState<Record<string, any[]>>({});
  const [myEntries, setMyEntries] = useState<Record<string, any[]>>({});
  const [user, setUser] = useState<any>(null);
  const [parentId, setParentId] = useState<string | null>(null);
  const [playerName, setPlayerName] = useState("");
  const [selectedGame, setSelectedGame] = useState<any>(null);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    fetchPtaData();
    checkUser();
  }, [ptaId]);

  useEffect(() => {
    if (ptaData?.id) fetchGames();
  }, [ptaData?.id, parentId]);

  useEffect(() => {
    if (user && ptaId) fetchParent();
  }, [user, ptaId]);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
  };

  const fetchParent = async () => {
    if (!user || !ptaId) return;
    const { data } = await supabase
      .from("parents").select("id, full_name")
      .eq("user_id", user.id).eq("pta_id", ptaId).maybeSingle();
    if (data) {
      setParentId(data.id);
      setPlayerName(data.full_name || "");
    }
  };

  const fetchPtaData = async () => {
    if (!ptaId) return;
    const { data } = await supabase.rpc("get_public_pta_info", { p_pta_id: ptaId }).single();
    if (data) setPtaData(data);
    setLoading(false);
  };

  const fetchGames = async () => {
    if (!ptaData?.id) return;
    const now = new Date().toISOString();
    const { data: allGames } = await supabase
      .from("guessing_games").select("*")
      .eq("pta_id", ptaData.id).eq("status", "active")
      .gt("end_date", now).order("created_at", { ascending: false });

    const data = (allGames || []).filter(g => !g.start_date || g.start_date <= now);
    setGames(data);

    const valMap: Record<string, any[]> = {};
    const myMap: Record<string, any[]> = {};
    let pendingCount = 0;

    for (const game of data) {
      // Validation query: only PAID entries count as taken
      const { data: valEntries } = await supabase
        .from("guessing_game_entries")
        .select("number_picked, guess_value")
        .eq("game_id", game.id)
        .eq("payment_status", "paid");
      valMap[game.id] = valEntries || [];

      // My entries query: full entries for current parent
      if (parentId) {
        const { data: mine } = await supabase
          .from("guessing_game_entries")
          .select("*")
          .eq("game_id", game.id)
          .eq("parent_id", parentId)
          .order("created_at", { ascending: false });
        myMap[game.id] = mine || [];
        pendingCount += (mine || []).filter(e => e.payment_status === "pending").length;
      }
    }

    // Also count pending entries from games not currently active (in case they added entries before)
    if (parentId) {
      const { count } = await supabase
        .from("guessing_game_entries")
        .select("id", { count: "exact", head: true })
        .eq("parent_id", parentId)
        .eq("payment_status", "pending");
      setCartCount(count || 0);
    }

    setValidationEntries(valMap);
    setMyEntries(myMap);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><p>Loading...</p></div>;
  }

  const headerColor = ptaData?.header_color || "#6366f1";

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="h-20 flex items-center justify-between px-6" style={{ backgroundColor: headerColor }}>
        <Link to={`/fundraising/${ptaId}`} className="flex items-center gap-3">
          {ptaData?.logo_url ? (
            <img src={ptaData.logo_url} alt={ptaData.name} className="h-12 w-auto object-contain" />
          ) : (
            <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
              <span className="text-lg">🏫</span>
            </div>
          )}
          <span className="font-semibold text-lg text-foreground hidden sm:inline-block">{ptaData?.name || "PTA"}</span>
        </Link>
        <div className="flex items-center gap-2">
          {/* Cart icon */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/fundraising/${ptaId}/games/cart`)}
            className="gap-2 relative"
          >
            <ShoppingCart className="h-5 w-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate(`/fundraising/${ptaId}`)} className="gap-2">
            <ArrowLeft className="h-4 w-4" /> Back
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 container py-8">
        {selectedGame ? (
          <GamePlayView
            game={selectedGame}
            entries={validationEntries[selectedGame.id] || []}
            myEntries={myEntries[selectedGame.id] || []}
            ptaId={ptaData.id}
            parentId={parentId}
            initialPlayerName={playerName}
            onBack={() => setSelectedGame(null)}
            onEntrySubmitted={() => fetchGames()}
            onGoToCart={() => navigate(`/fundraising/${ptaId}/games/cart`)}
          />
        ) : (
          <>
            {/* Hero */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-4">
                <Gamepad2 className="h-7 w-7 text-primary" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Games & Competitions</h1>
              <p className="text-muted-foreground text-lg max-w-md mx-auto">
                Pick your numbers or guess to win exciting prizes!
              </p>
            </div>

            {/* Gallery */}
            {games.length === 0 ? (
              <div className="text-center py-20 space-y-4">
                <div className="mx-auto w-20 h-20 rounded-full bg-muted flex items-center justify-center">
                  <Gamepad2 className="h-10 w-10 text-muted-foreground" />
                </div>
                <h2 className="text-xl font-semibold text-foreground">No active games right now</h2>
                <p className="text-muted-foreground">Check back soon for new games and competitions!</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {games.map(game => (
                  <GameCard
                    key={game.id}
                    game={game}
                    entryCount={(myEntries[game.id] || []).length}
                    onPlay={() => setSelectedGame(game)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-sm text-muted-foreground border-t border-border">
        <p>Powered by <span className="font-semibold text-foreground">The PTA Hub</span></p>
      </footer>
    </div>
  );
}
